import { CaretDownIcon, CheckIcon, CropIcon } from '@phosphor-icons/react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { useDialogContentContainer } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  type AspectRatioToken,
  type Placement,
  getPlacement,
  toMatrixPlatform,
} from '@/lib/platform-matrix/placement-matrix';
import { type MediaKind } from '@/lib/platform-matrix/placement-resolution';
import { cn } from '@/lib/utils';

interface AspectOption {
  /** Canonical short form persisted to media_keys.perPlatform[platform].aspectRatio */
  value: string;
  /** Visible chip label */
  label: string;
  /** One-liner describing intent — appears in dropdown only */
  description?: string;
  /** True when this option is the platform's algorithmic-safest default */
  recommended?: boolean;
}

/** Display-only copy for the dropdown (NOT a source of truth — the matrix is). */
const RATIO_DESCRIPTION: Record<AspectRatioToken, string> = {
  '1:1': 'Square — universal',
  '4:5': 'Portrait — fills more feed',
  '1.91:1': 'Landscape — link card',
  '9:16': 'Vertical — full screen',
  '16:9': 'Wide landscape',
  '2:3': 'Tall pin',
  '3:4': 'Portrait',
};

/**
 * Ratio chips for a platform's placement, derived from the canonical placement
 * matrix (the single source of truth). The first option is the recommended
 * suggestion. With no `placement`, resolves the platform's primary surface.
 */
function optionsForPlacement(platform: string, placement?: Placement): AspectOption[] {
  const p = toMatrixPlatform(platform);
  const row = p ? getPlacement(p, placement) : undefined;
  const tokens = row?.ratioOptions ?? (['1:1'] as const);
  return tokens.map((value, i) => ({
    value,
    label: value,
    description: RATIO_DESCRIPTION[value],
    recommended: i === 0,
  }));
}

export interface AspectRatioPickerProps {
  platform: string;
  /** Optional surface within the platform (feed/reel/story/short/…). Defaults to primary. */
  placement?: Placement;
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  /** Disables the trigger when true (e.g. read-only). */
  disabled?: boolean;
  /** True while a media_keys commit for this platform is in flight — trigger
   *  shows a Spinner and disables, mirroring PlacementPicker's contract
   *  (pre-merge review finding #8: a second commit must not look startable
   *  while the first's serialized refetch+write round trip is running). */
  isSaving?: boolean;
  /** The post's media kind — drives the locked-ratio tooltip copy (TikTok
   *  photo-mode review finding #6: the row's `videoOnly` lock now also
   *  applies to image/carousel posts on TikTok's single-ratio feed row, so
   *  the old hardcoded "for video" copy was factually wrong for them).
   *  Absent → the neutral copy, which is accurate for every media kind. */
  mediaKind?: MediaKind;
  className?: string;
}

/**
 * Per-platform aspect-ratio chip dropdown. Persists the chosen short-form
 * ratio (e.g. `'4:5'`) to `media_keys.perPlatform[platform].aspectRatio`.
 * Absence means the original media, not an implied recommended crop. The
 * PlatformPreview replica reads an explicit ratio via the `aspectRatio` prop
 * and re-renders at the new crop. Phase B of DS-21 — see
 * `docs/platform-apis/SPEC-aspect-ratio-pipeline.md`.
 */
export const AspectRatioPicker: React.FC<AspectRatioPickerProps> = ({
  platform,
  placement,
  value,
  onChange,
  disabled,
  isSaving,
  mediaKind,
  className,
}) => {
  const container = useDialogContentContainer();
  const matrixPlatform = toMatrixPlatform(platform);
  const row = matrixPlatform ? getPlacement(matrixPlatform, placement) : undefined;
  const options = optionsForPlacement(platform, placement);
  const recommended = options.find((o) => o.recommended);
  const resolved = value ?? 'Original';
  const isLocked = row?.videoOnly ?? false;
  const isTriggerDisabled = !!disabled || isLocked || !!isSaving;

  const trigger = (
    <DropdownMenuTrigger asChild disabled={isTriggerDisabled}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn('h-7 px-2 gap-1 text-detail', className)}
      >
        {isSaving ? <Spinner size="sm" /> : <CropIcon className="size-3.5" weight="regular" />}
        <span>{resolved}</span>
        {!isTriggerDisabled ? <CaretDownIcon className="size-3" weight="bold" /> : null}
      </Button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu>
      {isLocked ? (
        // A native `disabled` button never fires pointer events, so a
        // Tooltip on the button itself would never show — wrap it in a
        // span so hover still reaches something, and explain WHY it's
        // locked (previously just showed disabled with no explanation).
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex" tabIndex={0}>
              {trigger}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {/* Media-kind-aware: "for video" only when the post IS video —
                a TikTok image/carousel hits this same videoOnly-row lock
                (single 9:16 ratio option) and must not be told it's video. */}
            {value
              ? mediaKind === 'video'
                ? `Crop is fixed at ${resolved} for video`
                : `Crop is fixed at ${resolved} for this placement`
              : `Use a ${recommended?.label ?? 'supported'} source for this placement`}
          </TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}
      <DropdownMenuContent align="start" className="w-64" container={container}>
        <DropdownMenuLabel className="text-micro uppercase tracking-wider text-muted-foreground">
          Crop ratio
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => onChange(undefined)}
          className="flex items-start gap-2 py-2"
        >
          <span className="mt-0.5 size-3.5 shrink-0 flex items-center justify-center">
            {!value ? <CheckIcon className="size-3.5" weight="bold" /> : null}
          </span>
          <span className="flex-1 min-w-0">
            <span className="text-sm font-medium text-foreground">Original</span>
            <span className="block text-detail text-muted-foreground">
              Send the original media without a Mora crop
            </span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {options.map((option) => {
          const isCurrent = option.value === value;
          return (
            <DropdownMenuItem
              key={option.value}
              onSelect={() => onChange(option.value)}
              className="flex items-start gap-2 py-2"
            >
              <span className="mt-0.5 size-3.5 shrink-0 flex items-center justify-center">
                {isCurrent ? <CheckIcon className="size-3.5" weight="bold" /> : null}
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1.5">
                  <span className="text-sm font-medium text-foreground">{option.label}</span>
                  {option.recommended ? (
                    <span className="text-micro text-muted-foreground/80">Recommended</span>
                  ) : null}
                </span>
                {option.description ? (
                  <span className="block text-detail text-muted-foreground">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export { optionsForPlacement };
